import React, { useState, useEffect, useRef, useContext } from 'react'
import { Button } from 'syngenta-react-components'
import clear from '../../assets/icons/clear.svg'
import Select from '../../dpe-usco/components/Select/Select'
import { getUserRoles, inviteUser, getUserProfile, searchUsers, addNewUser } from '../../services/apiHelper'
import { useForm, Controller } from 'react-hook-form'
import TypeAhead from '../../dpe-usco/components/TypeAhead/TypeAhead'
import TableStatus from '../subComponents/TableStatus/TableStatus'
import { GlobalContext } from '../../services/context'

const NewUserForm = ({ modalType, handleClose, companyId }) => {
  const { trackEvent } = useContext(GlobalContext)
  const [activeRole, setActiveRole] = useState()
  const [existingUsers, setExistingUsers] = useState()
  const [rolesData, setRolesData] = useState()
  const [roleOptions, setRoleOptions] = useState([])
  const [roleId, setRoleID] = useState()
  const [selectedUser, setSelectedUser] = useState()
  const [selectedFromSearch, setSelectedFromSearch] = useState()
  const [stopSearch, setStopSearch] = useState(false)
  const [invitationStatus, setInvitationStatus] = useState()
  const { register, errors, clearError, handleSubmit, reset, setValue, watch, control } = useForm({})

  const watchUserName = watch('name', '')
  
  const canceled = useRef(false)
  useEffect(()=> {
    return () => {
      canceled.current = true
    }
  }, [])

  useEffect(() => {
    if (watchUserName.length > 2 && !stopSearch) {
      searchUsers(watchUserName, 'full_name' && 'email')
        .then(res => {
          if(res.length && !canceled.current){
            setExistingUsers(res)
          } 
          else if (!res.length && !canceled.current){
            searchUsers(watchUserName, 'email')
              .then(res => {
                if(res && !canceled.current){
                  setExistingUsers(res)
                }
              })
          }
        })
      if(!canceled.current){
        setStopSearch(true)
      }
    }
  }, [watchUserName, stopSearch])

  useEffect(() => {
    const initData = () => {
      getUserRoles()
        .then(res => {
          if(res?.length && !canceled.current){
            const alphabeticalData = [...res]
            alphabeticalData.sort((a, b) => {
              return a.role_name > b.role_name ? 1 : -1
            })
            setRolesData(() => {
              return alphabeticalData
            })
          }
        })
    }
    initData()
  }, [setRolesData])

  useEffect(() => {
    if (rolesData && modalType && (modalType === 'linkedContacts')) {
      const roleNames = rolesData.filter(role => role.role_type === 'internal').map(role => {
        return role.role_name
      })
      if(!canceled.current){
        setRoleOptions(roleNames)
      }
    } else if (rolesData && modalType && (modalType === 'employees')){
      const roleNames = rolesData.filter(role => role.role_type === 'external'
      ).map(role => {
        return role.role_name
      })
      if(!canceled.current){
        setRoleOptions(roleNames)
      }
    }
  }, [
    modalType, rolesData, setRoleOptions
  ])

  useEffect(() => {
    if (rolesData && activeRole) {
      const matchedRoleId = rolesData.filter(role => activeRole === role.role_name).map(role => {
        return role.role_id
      })
      if(!canceled.current){
        setRoleID(matchedRoleId)
      }
    }
  }, [activeRole, rolesData])

  const roleSelection = value => {
    setActiveRole(value)
    setValue('role', value)
  }

  const userSelection = value => {
    setSelectedUser(value)
  }

  const verifyExistingUser = async formValues => {
    setInvitationStatus('LOADING')
    if(selectedFromSearch && selectedUser === 'existing_user'){
      const data = await getUserProfile(selectedFromSearch)
      if (data) {
        let role_id = roleId[0]
        let company_id = companyId
        let user_id = data.user_id
        const response = await inviteUser({ role_id, company_id, user_id })
        if(!canceled.current){
          if(response && response !== 400){
            setInvitationStatus('SUCCESS')
          } else if (response && response === 400){
            setInvitationStatus('FAILED_USER_ALREADY_ASSIGNED')
          } else {
            setInvitationStatus('FAILED')
          }
        }
      } else {
        if(!canceled.current){
          setInvitationStatus('FAILED')
        }
      }
    } else if(!selectedFromSearch && selectedUser === 'existing_user'){
      setInvitationStatus('FAILED_USER_DOESNT_EXIST')
    }
    else if (selectedUser === 'new_user'){
      const userIdFromResponse = await addNewUser(formValues)
      if (userIdFromResponse && userIdFromResponse !== 409) {
        let role_id = roleId[0]
        let company_id = companyId
        let user_id = userIdFromResponse
        const response = await inviteUser({ role_id, company_id, user_id })
        if(!canceled.current){
          if(response.company_id){
            setInvitationStatus('SUCCESS')
          } else {
            setInvitationStatus('FAILED')
          }
        }
      } else {
        if(!canceled.current){
          setInvitationStatus('FAILED_USER_ALREADY_EXISTS')
        }
      }
    }
  }

  const onSubmit = formValues => {
    let email = formValues.email
    if (activeRole) {
      verifyExistingUser(email)
      const prefix = modalType === 'linkedContacts' ? 'linked_contact' : 'employee'
      trackEvent(`${prefix}_invited`, { companyId, ...formValues, type: modalType })
    } 
  }

  const handleCloseModal = result => {
    canceled.current = true
    reset()
    clearError()
    setActiveRole(null)
    handleClose(result)
  }

  const _nameCallback = val => {
    setValue('name', val.name)
    setSelectedFromSearch(val.user_email)
  }

  const handleSelectUserType = type=> {
    userSelection(type)
    trackEvent(`${type}_selected`, { companyId, type: modalType })
  }

  const modalDecider = () => {
    if (selectedUser) {
      return (
        <div className='p16 df jcsb fxdrc fxww bd1-s-lightgray bdrs-4 m15'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='df fxdrr jcsb'>
              <div className='w45% tal'>
                <label className='fz13'>Role*</label>
                <div className='fz15'>
                  <Controller
                    as={<Select/>}
                    placeholder='Select role'
                    handleSelection={roleSelection}
                    options={roleOptions}
                    control={control}
                    name='role'
                    id='role'
                    rules={{ required: true }}
                  />
                  <div className='c-red tal fz12 pl2'>
                    {errors.role && errors.role.type === 'required' && 'Please select a role for this user'}
                  </div>
                </div>
              </div>
              <div className='w45% tal'>
                <div className='tal w45%'>
                </div>
                {selectedUser === 'new_user' 
                  ? 
                  (
                    <div>
                      <label className='fz13'>Email*</label>
                      <input
                        name="email"
                        ref={register({ required: true, pattern: /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/ })}
                      />
                    </div>
                  )
                  : 
                  (
                    <div className='mt20'>
                      <TypeAhead className='fz15 lh1.4' id='name' name='name' placeholder='Search user'
                        ref={register({ required: true })}
                        item_id='user_id'
                        item_value='name'
                        item_value_additional='user_email'
                        data={existingUsers}
                        callback={_nameCallback}
                        onChange={() => setStopSearch(false)}
                      />
                    </div>
                  )
                }
                <div className='c-red tal fz13 pl4'>
                  {errors.email && errors.email.type === 'required' && 'Please provide email address'}
                  {errors.email && errors.email.type === 'pattern' &&
                    'This email appears to be invalid. Please check if anything is missing'
                  }
                </div>
              </div>
            </div>
          </form>
        </div>
      ) 
    }
  }

  let bottomButtons = (
    <>
      <Button text='Cancel' customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4' clickHandler={handleCloseModal}/>
      <Button type='success' text='Invite user' clickHandler={handleSubmit(onSubmit)} customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4'/>
    </>
  )
  let content = (
    <>
      <div className='df fxdrr aifs'>
        <input type='radio' id="existing_user" name="user_type" value="existing_user" onChange={() => handleSelectUserType('existing_user')} className='pt5 ml15 tal w5%' />
        <label htmlFor='existing_user'>Existing user</label>
      </div>
      {selectedUser === 'existing_user'
        ?
        modalDecider()
        :
        null
      }
      <div className='df fxdrr aifs'>
        <input type='radio' id="new_user" name="user_type" value="new_user" onChange={() => handleSelectUserType('new_user')} className='pt5 ml15 tal w5%' />
        <label htmlFor='new_user'>New user</label>
      </div>
      {selectedUser === 'new_user'
        ?
        modalDecider()
        :
        null
      }
    </>
  )
  switch(invitationStatus){
    case 'LOADING':
      content = (
        <div>
          <p>Invitation in progress. Please wait.</p>
          <TableStatus status='LOADING'/>
        </div>
      )
      bottomButtons = (
        <Button type='success' text='Close' clickHandler={handleCloseModal} customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4'/>
      )
      break
    case 'SUCCESS':
      content = (
        <div className='df fxdrr jcsb'>
          <div className='w100% tac'>
            <p>Successfully added the user.</p>
          </div>
        </div>
      )
      bottomButtons = (
        <Button type='success' text='Close' clickHandler={() => handleCloseModal('SUCCESS')} customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4'/>
      )
      break
    case 'FAILED':
      content = (
        <div className='df fxdrr jcsb'>
          <div className='w100% tac'>
            <p>Something went wrong. Please try again later.</p>
          </div>
        </div>
      )
      bottomButtons = (
        <Button type='success' text='Close' clickHandler={handleCloseModal} customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4'/>
      )
      break
    case 'FAILED_USER_ALREADY_EXISTS':
      content = (
        <div className='df fxdrr jcsb'>
          <div className='w100% tac'>
            <p>This user already exists in the system.</p>
            <p>Please start over and select Existing user instead of New user.</p>
          </div>
        </div>
      )
      bottomButtons = (
        <Button type='success' text='Close' clickHandler={handleCloseModal} customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4'/>
      )
      break
    case 'FAILED_USER_DOESNT_EXIST':
      content = (
        <div className='df fxdrr jcsb'>
          <div className='w100% tac fz17'>
            <p>You attempted to invite a user who is currently not in the system.</p>
            <p>Please start over and add this person as a New user.</p>
          </div>
        </div>
      )
      bottomButtons = (
        <Button type='success' text='Close' clickHandler={handleCloseModal} customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4'/>
      )
      break
    case 'FAILED_USER_ALREADY_ASSIGNED':
      content = (
        <div className='df fxdrr jcsb'>
          <div className='w100% tac fz17'>
            <p>User with email address <b> {selectedFromSearch} </b>is already assigned to this company.</p>
          </div>
        </div>
      )
      bottomButtons = (
        <Button type='success' text='Close' clickHandler={handleCloseModal} customClassName='mb8 mr8 pl16 pr16 pb10 pt10 fz16 fw600 bdrs-4'/>
      )
      break
    default:
      break
  }

  return (
    <div>
      <div className='w100% h100% bgc-white bd1-s-lightgray bxshmodal bdrs-4'>
        <div className='df jcsb'>
          <p className='fz16 ml16 fw600'>Invite user</p>
          <button className='bdnone m0 bgc-t' onClick={handleCloseModal}>
            <img src={clear} alt='cancel button' />
          </button>
        </div>
        <hr className='mt0 mb16 bdnone bt1-s-lightgray' />
        {content}
        <hr className='mt20 mb16 bdnone bt1-s-lightgray' />
        <div className='df jcfe'>
          {bottomButtons}
        </div>
      </div>
    </div>
  )
}

export default NewUserForm